import React, { DetailedHTMLProps, HTMLAttributes, useCallback, useEffect, useRef } from 'react'
import { useMount, useUnmount } from './hooks/useMount'
import { useZoomSession } from './SessionProvider'
import { SessionVideoTile } from './SessionVideoTile'

/**
 * Props interface for the SessionVideoPlayer component
 * Defines the required properties for rendering an individual participant's video stream
 */
interface SessionVideoPlayerProps {
  /** Participant object containing user information and video/audio state */
  user: any
  /** Zoom Stream API instance for managing video stream attachment/detachment */
  stream: any
  /** Network quality score for this user (0-3, where 3 is best quality) */
  networkQuality: number
  /** Flag indicating if this is the spotlight video (affects sizing and display behavior) */
  isSpotlightVideo: boolean
}

/**
 * Global type declaration for Zoom Video SDK custom elements
 * Extends JSX to recognize the custom video-player element from Zoom SDK
 * This allows TypeScript to properly type the custom HTML elements used by Zoom
 */
declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<any>, any> & {
        class?: string
      }
    }
  }
}

/**
 * SessionVideoPlayer Component
 *
 * Individual video player component that manages the video stream for a single participant
 * in a Zoom session. This component handles the complete lifecycle of video stream management
 * including attachment, detachment, and state synchronization with the Zoom SDK.
 *
 * Key responsibilities:
 * - Manages video stream attachment/detachment based on user's video state
 * - Provides visual feedback when video is off (dark background with info tile)
 * - Handles high-quality video rendering (1080P when available)
 * - Displays network quality and mute status indicators
 * - Automatically cleans up video resources on component unmount
 *
 * The component uses Zoom's custom video-player elements for optimal video rendering
 * and integrates with the SessionVideoTile for status information display.
 */
export const SessionVideoPlayer: React.FC<SessionVideoPlayerProps> = ({
  user,
  stream,
  networkQuality,
  isSpotlightVideo,
}) => {
  const { logger } = useZoomSession()

  // Reference to the Zoom video player element for stream attachment
  const videoPlayerRef = useRef<any | null>(null)
  // Track whether video stream is currently attached to prevent visual flicker
  // This state controls the opacity of the video container for smooth transitions
  const [isVideoAttached, setIsVideoAttached] = React.useState(false)

  /**
   * Handles video stream attachment and detachment based on user's video state
   * This function is the core logic for managing video streams:
   * - Attaches video stream when user has video enabled
   * - Detaches video stream when user turns off video
   * - Uses 1080P quality for optimal video experience
   */
  const handleVideoStateChange = useCallback(async () => {
    logger.info('Handling video for user', { user })

    if (videoPlayerRef && videoPlayerRef.current && user.bVideoOn) {
      // User has video enabled - attach the stream with high quality
      // VideoQuality.Video_1080P ensures the best possible video resolution
      logger.info('Attaching video', { user })
      await stream.attachVideo(user.userId, 4, videoPlayerRef.current)
      // Re-set the video player defaults so that the sdk does not attempt to override the aspect ratio
      if (isSpotlightVideo) {
        videoPlayerRef.current.style.height = '100%'
        videoPlayerRef.current.style.width = '100%'
      } else {
        videoPlayerRef.current.style.height = '125px'
        videoPlayerRef.current.style.width = 'auto'
      }
      // Set attached state to make video visible (opacity: 1)
      setIsVideoAttached(true)
    } else {
      // User has video disabled - "ideally" detach the stream to free resources
      // BUT ... zoom detaches based on the userId (and not the user guid)
      // meaning if the same user were to join from two places at once (ex: their ipad and their app)
      // and then disconnect from one device - calling detach with the userId could remove
      // both of these streams
      // Set detached state to hide video container (opacity: 0)
      setIsVideoAttached(false)
    }
  }, [stream, user])

  // Initialize video stream when component mounts
  useMount(() => {
    handleVideoStateChange()
  })

  /**
   * Cleanup video resources when component unmounts
   * Ensures proper cleanup to prevent memory leaks and resource conflicts
   * Handles both cases: with and without video player reference
   */
  useUnmount(() => {
    logger.info('Not detaching video on unmount', { user })
    // See comment about detaching in handleVideoStateChange
  })

  /**
   * React to changes in user state or stream instance
   * Re-evaluates video attachment whenever:
   * - User's video state changes (bVideoOn)
   * - User object changes (different participant)
   * - Stream instance changes
   */
  useEffect(() => {
    handleVideoStateChange()
  }, [handleVideoStateChange, stream, user])

  return (
    <>
      {/*
        Zoom video player container with conditional visibility
        Opacity transitions provide smooth show/hide effects when video state changes
      */}
      <video-player-container
        style={
          {
            opacity: isVideoAttached ? 1 : 0, // Smooth transition when video attaches/detaches
            height: '100%',
            backgroundColor: 'black', // Black background for video area
          } as React.CSSProperties
        }
      >
        {/*
          Zoom SDK video player element with conditional sizing
          Self-video gets fixed height (125px) for picture-in-picture display
          Other participants get full container dimensions for main video area
        */}
        <video-player
          ref={videoPlayerRef}
          style={{
            height: isSpotlightVideo ? '100%' : '125px', //  Full height for spotlight video, fixed height for others
            width: isSpotlightVideo ? '100%' : undefined, // Full width for spotlight video, auto width for others
            display: 'block',
            position: 'relative',
            zIndex: 100, // Ensure video appears above background elements
          }}
        />
      </video-player-container>
      {/*
        Overlay tile showing participant status information
        Displays mute status and network quality indicators over the video
      */}
      <SessionVideoTile muted={!!user.muted} quality={networkQuality} />
    </>
  )
}
